import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { Kontext, SichtbarKontext, type ToastAktion } from "./toastKontext";
import { FEDER, UEBERBLENDUNG } from "./federn";
import { DAUER, DAUER_BEDIENUNG, kurveAlsListe, kurveFuer } from "../welt/bewegung";
import { weltLesen } from "../welt/einstellungen";


/** Toast (Handoff Schiefer): unten mittig ueber der Tab-Leiste, Flaeche mit Schatten e2, Radius 10,
 *  H 48, Text 15, optional eine Aktion; 4 s sichtbar, role=status. Die Flaeche nimmt keine Klicks an
 *  (nur die Aktion), damit sie nichts darunter verdeckt; die Pause bei Hover entfaellt deshalb.
 *  Mit Aktion (W9: „Plan ansehen“) bleibt sie laenger stehen und der Link ist bedienbar. */
export function ToastAnbieter(props: { children: ReactNode; navigieren: (zu: string) => void }) {
  const [text, setText] = useState<string | null>(null);
  const [aktion, setAktion] = useState<ToastAktion | undefined>(undefined);
  const uhr = useRef<ReturnType<typeof setTimeout>>(undefined);
  const ruhig = useReducedMotion();
  const { navigieren } = props;
  // W82: steht eine Seite in der W82-Welt (data-welt="wurzel"), zeigt sich die Meldung als Hinweis der App (welt.css):
  // glasDicht, unten mittig, 2,8 s ohne Aktion, Deckkraft 300 und Lage 500 aus 12 px (ruhig 10 px)
  const [welt, setWelt] = useState<false | "lebhaft" | "ruhig">(false);
  const zeigen = useCallback((neu: string, weg?: ToastAktion) => {
    const w82 = document.querySelector('[data-welt="wurzel"]') ? weltLesen().fassung : false;
    setWelt(w82);
    setText(neu);
    setAktion(weg);
    clearTimeout(uhr.current);
    // R1 W81-W-2: lange Saetze brauchen Lesezeit (50 ms je Zeichen); alle Meldungen bis 56 Zeichen behalten ihre Dauer
    const dauer = weg ? ("tun" in weg ? 9000 : 6000) : w82 ? DAUER.blasen.lebhaft.hinweisSichtbar : 4000;
    uhr.current = setTimeout(() => setText(null), Math.max(dauer, neu.length * 50));
  }, []);
  const lage = welt === "ruhig" ? 10 : 12;
  const weltUebergang = welt && {
    opacity: { duration: DAUER_BEDIENUNG.hinweisBlende / 1000, ease: kurveAlsListe("expo") },
    y: { duration: DAUER_BEDIENUNG.hinweisLage / 1000, ease: kurveAlsListe(kurveFuer("blase", welt)) },
  };
  return (
    <Kontext.Provider value={zeigen}>
      {/* W73d (D-314 H1): ob eine Meldung steht (das Blatt „Nutzungsstatistik“ wartet, bis sie weg ist) */}
      <SichtbarKontext.Provider value={text !== null}>{props.children}</SichtbarKontext.Provider>
      <div
        aria-live="polite"
        data-welt={welt ? "" : undefined}
        // W82 Web-5b (K5): unten mindestens `--toast-ueber` (setzt eine Seite mit eigener Knopfreihe, etwa die Einführung)
        className={`toast-ebene pointer-events-none fixed inset-x-4 z-50 flex justify-center ${welt ? "welt-hinweise" : ""}`}
      >
        <AnimatePresence>
          {text && (
            <motion.div
              key={text}
              initial={ruhig ? { opacity: 0 } : { opacity: 0, y: welt ? lage : 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: ruhig ? 0 : welt ? lage : 16 }}
              transition={ruhig ? UEBERBLENDUNG : (weltUebergang || FEDER.sheetAuf)}
              data-schwebend={welt ? "" : undefined}
              // R1 W82-Web-5b: bei großer Schrift bricht der Satz um (auch in einer langen Adresse) und die Aktion rückt darunter,
              // statt dass die Meldung links und rechts über den Rand ragt (bausteine.css `.toast-text`)
              className={`t-subheadline flex min-h-(--pg-hit) max-w-full flex-wrap items-center gap-x-3 rounded-md bg-pg-surface px-4 py-1 text-pg-text shadow-e2 ${welt ? "welt-hinweis glas" : ""}`}
              role="status"
            >
              <span className="toast-text">{text}</span>
              {aktion && (
                <button
                  type="button"
                  className="pointer-events-auto inline-flex min-h-(--touch) shrink-0 items-center font-semibold whitespace-nowrap text-pg-accent-text hover:opacity-70"
                  onClick={() => {
                    setText(null);
                    if ("tun" in aktion) aktion.tun();
                    else navigieren(aktion.zu);
                  }}
                >
                  {aktion.text}
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Kontext.Provider>
  );
}
